// A session's transcript: its items in order, a run of actions folded into
// one row, and the end kept in view while he reads it. Once he scrolls up,
// or opens something by hand, the page stops pulling to the end and offers
// "Jump to latest". Moved from today's Code tab (code/Transcript.tsx
// there). Today's "Show earlier" has no counterpart: the server reads a
// transcript forward only, and the page reads it from its start.
//
// A live region reads out the agent's text as each reply is complete.
//
// An expired question shows where it was asked (questions/expired.ts).
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { Me, Question } from '../api/types'
import type { AskRecord } from '../questions/asks'
import { END, placeExpired } from '../questions/expired'
import { Working } from '../components/Working'
import { renderMarkdown } from '../markdown/render'
import type { TranscriptView } from '../sessions/follow'
import { ChevronDownIcon } from './icons'
import { lastReplies, toBlocks, toRows, type Row } from './items'
import { ActionGroup, AskRecords, ItemView, TranscriptContext, type TranscriptActions } from './TranscriptItems'
import type { AssistantItem, Item, UserItem } from './types'

/** How near its end a transcript must be scrolled for the page to keep following it. */
const FOLLOW_PX = 48

export interface TranscriptProps {
  view: TranscriptView
  me: Me | null
  /** The session runs: its last text may still be written, and its last reply is not yet known. */
  busy?: boolean
  /** Told whether he reads the latest. */
  onFollowing?: (following: boolean) => void
  /** What a prompt that carried an answer to the session's question shows instead: the question and its answer, when known. */
  askRecord?: (item: UserItem) => AskRecord | null
  /** Given, a shell block in a reply has Run, which calls this with its command. */
  onRun?: (command: string) => void
  /** The session's expired questions, shown where they were asked: when each was asked, when known, and how each shows. */
  expired?: ExpiredQuestions
}

export interface ExpiredQuestions {
  questions: readonly Question[]
  askedAt: (id: string) => string | null
  render: (question: Question) => ReactNode
}

function atEnd(box: HTMLElement): boolean {
  return box.scrollHeight - box.scrollTop - box.clientHeight <= FOLLOW_PX
}

/** The rows of the items; a row whose item and calls did not change is the object it was. */
function useRows(items: readonly Item[]): Row[] {
  const known = useRef(new WeakMap<Item, Row>())
  return useMemo(() => {
    const stable = (row: Row): Row => {
      const children = row.children.map(stable)
      const before = known.current.get(row.item)
      if (before && before.children.length === children.length && before.children.every((child, i) => child === children[i])) return before
      const next = { item: row.item, children }
      known.current.set(row.item, next)
      return next
    }
    return toRows(items).map(stable)
  }, [items])
}

export function Transcript({ view, me, busy = false, onFollowing, askRecord, onRun, expired }: TranscriptProps) {
  const { items, status, error, cwd } = view
  const box = useRef<HTMLDivElement>(null)
  const content = useRef<HTMLDivElement>(null)
  const following = useRef(true)
  /** The height of what scrolls, as last seen: where its end was before it grew. */
  const lastHeight = useRef(0)
  const [away, setAway] = useState(false)
  const rows = useRows(items)
  const blocks = useMemo(() => toBlocks(rows), [rows])
  const last = useMemo(() => lastReplies(items, busy), [items, busy])
  const said = useNewText(items, status === 'ready')
  const placed = useMemo(() => (expired && expired.questions.length > 0 ? placeExpired(blocks, expired.questions, expired.askedAt) : null), [blocks, expired])
  const after = (at: number) => placed?.get(at)?.map((question) => <Fragment key={`expired-${question.id}`}>{expired!.render(question)}</Fragment>)

  const follow = useCallback(
    (next: boolean) => {
      if (following.current !== next) onFollowing?.(next)
      following.current = next
      setAway(!next)
    },
    [onFollowing],
  )

  /**
   * Keep the end in view while he follows it. Where he is decides, against
   * the height last seen: he may have scrolled up a moment ago, with that
   * scroll's event still on its way, and what he went to read must not be
   * pulled from under him.
   */
  const pin = useCallback(() => {
    const el = box.current
    if (!el) return
    if (following.current) {
      if (lastHeight.current - el.scrollTop - el.clientHeight <= FOLLOW_PX) el.scrollTop = el.scrollHeight
      else follow(false)
    }
    lastHeight.current = el.scrollHeight
  }, [follow])

  // After every change: stay at the end while following.
  useLayoutEffect(() => {
    pin()
  }, [items, status, pin])

  // What grows without a change of items (a long text drawn a moment later) keeps the end in view too.
  useEffect(() => {
    const inner = content.current
    if (!inner || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(pin)
    observer.observe(inner)
    return () => observer.disconnect()
  }, [status, pin])

  const onScroll = () => {
    const el = box.current
    if (!el) return
    lastHeight.current = el.scrollHeight
    follow(atEnd(el))
  }

  // He opened or closed something: what he looks at must not move under him.
  const onFold = useCallback(() => {
    following.current = false
    onFollowing?.(false)
    // Whether that left him at the end is known once it is drawn.
    const later = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (run: () => void) => setTimeout(run, 0)
    later(() => {
      const el = box.current
      if (!el) return
      lastHeight.current = el.scrollHeight
      follow(atEnd(el))
    })
  }, [follow, onFollowing])

  const jump = () => {
    const el = box.current
    if (!el) return
    el.scrollTop = el.scrollHeight
    lastHeight.current = el.scrollHeight
    follow(true)
  }

  const actions = useMemo<TranscriptActions>(() => ({ cwd, me, busy, onFold, onRun }), [cwd, me, busy, onFold, onRun])

  let body
  if (items.length === 0 && status === 'error') {
    body = (
      <div className="code-state" role="alert">
        <p>
          <strong>The session could not be read.</strong>
        </p>
        {error && (
          <p className="muted">
            {error.message} <code>{error.code}</code>
          </p>
        )}
      </div>
    )
  } else if (items.length === 0 && status === 'loading') {
    body = (
      <div className="code-state" aria-busy="true">
        <Working />
      </div>
    )
  } else if (items.length === 0) {
    body = <p className="code-state muted">Nothing yet.</p>
  } else {
    body = (
      <TranscriptContext.Provider value={actions}>
        <AskRecords.Provider value={askRecord ?? null}>
          {blocks.map((block, at) => (
            <Fragment key={block.kind === 'group' ? `group-${block.rows[0]!.item.id}` : block.row.item.id}>
              {block.kind === 'group' ? <ActionGroup rows={block.rows} /> : <ItemView row={block.row} last={last.has(block.row.item.id)} />}
              {after(at)}
            </Fragment>
          ))}
        </AskRecords.Provider>
      </TranscriptContext.Provider>
    )
  }

  return (
    <div className="code-transcript-frame">
      {/* Focusable, so the keyboard scrolls it. */}
      <div className="code-transcript" ref={box} onScroll={onScroll} tabIndex={0} role="region" aria-label="Transcript">
        <div className="code-transcript-items" ref={content}>
          {body}
          {after(END)}
          {items.length > 0 && view.local && <p className="muted">From this Mac's runner.</p>}
          {items.length > 0 && view.offline && !view.local && <p className="muted">Offline: what this device kept of this session.</p>}
        </div>
      </div>
      {away && status === 'ready' && (
        <button type="button" className="button code-jump" onClick={jump}>
          <ChevronDownIcon size={12} />
          Jump to latest
        </button>
      )}
      <div className="visually-hidden" aria-live="polite">
        {said && <p key={said.key}>{said.text}</p>}
      </div>
    </div>
  )
}

/** A text's words, without its Markdown: what the live region reads. The document is inert: nothing in it loads. */
function plainText(markdown: string): string {
  const doc = new DOMParser().parseFromString(renderMarkdown(markdown, { localImagesOnly: true }), 'text/html')
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim()
}

/**
 * The agent's text that came in while the page followed the transcript, once
 * each reply is complete: when something follows it. What the transcript
 * held when it caught up is not news.
 */
function useNewText(items: readonly Item[], ready: boolean): { key: string; text: string } | null {
  const known = useRef<Set<string> | null>(null)
  const [said, setSaid] = useState<{ key: string; text: string } | null>(null)
  useEffect(() => {
    if (!ready) return
    const complete = items.slice(0, -1).filter((item): item is AssistantItem => item.kind === 'assistant')
    if (known.current === null) {
      known.current = new Set(complete.map((item) => item.id))
      return
    }
    const seen = known.current
    const fresh = complete.filter((item) => !seen.has(item.id))
    if (fresh.length === 0) return
    for (const item of fresh) seen.add(item.id)
    const text = fresh
      .map((item) => plainText(item.text))
      .filter(Boolean)
      .join(' ')
    if (text) setSaid({ key: fresh.at(-1)!.id, text })
  }, [items, ready])
  return said
}
